import cx from 'classnames'
import Icon from 'components/Icon'
import { FormGroupContainer, FormRow } from 'components/inputs'
import {
  CustomSelect,
  RenderSearchItemProps,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import { observer } from 'mobx-react'
import React from 'react'
import styles from './TransferProDialog.sass'
import Member from 'iam/models/Member'
import Avatar, { Badge } from 'components/Avatar'
import ProInputEditorViewModel from './ProInputEditorViewModel'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'

interface ProInputEditorProps {
  vm: ProInputEditorViewModel
}

export default observer(function ProInputEditor({ vm }: ProInputEditorProps) {
  const selectProProps = useCustomSelectStateFromItemSource({
    itemSource: vm.canDisplayAllPros ? vm.fetchPros : vm.fetchQualifiedPros,
  })

  return (
    <>
      <FormRow>
        <FormGroupContainer model={vm} field="currentChampionPro">
          <div className={styles.memberInfo}>
            <div className={styles.avatarContainer}>
              <Avatar
                badge={
                  vm.currentChampionPro?.user.online
                    ? Badge.Online
                    : Badge.Offline
                }
                size={40}
                subject={{
                  avatar: vm.currentChampionPro?.avatar,
                  displayName: vm.currentChampionPro?.displayName,
                }}
              />
            </div>
            <div className={styles.memberContent}>
              <strong className={styles.memberTitle}>
                {vm.currentChampionPro?.displayName}
              </strong>
              <p className={styles.memberSubtitle}>Current Pro</p>
            </div>
          </div>
        </FormGroupContainer>
      </FormRow>
      <FormRow className={styles.row}>
        <div className={styles.proFilterTitle}>
          What kind of Pro are you looking for?
        </div>
        <RadioButtonGroup size="big-wide">
          <RadioButtonItem
            onClick={vm.setDisplayQualifiedPros}
            active={vm.canDisplayQualifiedPros}
            dataTest="transfer-to-pro-select-qualified"
          >
            Qualified only
          </RadioButtonItem>
          <RadioButtonItem
            onClick={vm.setDisplayAllPros}
            active={vm.canDisplayAllPros}
            dataTest="transfer-to-pro-select-all"
          >
            Any available
          </RadioButtonItem>
        </RadioButtonGroup>
      </FormRow>
      <FormRow className={styles.row}>
        <FormGroupContainer
          model={vm}
          field="pro"
          className={styles.selectProContainer}
          error={vm.validation.getError('pro')}
        >
          <div className={styles.newProLabel}>New Pro</div>
          <CustomSelect
            {...selectProProps}
            multi={false}
            icon={<Icon name="dropdown" className={cx(styles.icon)} />}
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                item={itemProps}
                itemToString={(i) => String(i.item?.displayName)}
                visual={<Avatar size={24} subject={itemProps.item} />}
              />
            )}
            placeholder={
              vm.canDisplayAllPros
                ? 'Search any available'
                : 'Search qualified only'
            }
            value={vm.pro}
            onChange={vm.setPro}
            renderSearchItem={(props) => <TransferToProSearchItem {...props} />}
            data-test="transfer-to-pro-dropdown"
          />
        </FormGroupContainer>
      </FormRow>
    </>
  )
})

interface TransferToProSearchItem
  extends RenderSearchItemProps<Member | null> {}

const TransferToProSearchItem = React.forwardRef(
  (
    { item, itemToString, ...itemProps }: TransferToProSearchItem,
    ref: React.Ref<any>
  ) => {
    return (
      <SearchResultItem
        ref={ref}
        {...itemProps}
        visual={<Avatar size={24} subject={item} />}
        data-test={
          'transfer-to-pro-option-' + item?.user.publicId?.toLowerCase()
        }
      >
        <>
          <div className={styles.proName}>{item?.displayName}</div>
          <div className={styles.proEmail}>{item?.user.email}</div>
        </>
      </SearchResultItem>
    )
  }
)
